import { useMemo } from 'react';
import { LangType } from '../../../../../../helpers/lang';
import { useLang } from '../../../../../../hooks/use-lang';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import { generateColors, IgnoredTasksType, withBarSelection } from '../../dashboard-helpers';
import { useFetchDashboardData } from '../../use-fetch-dashboard';
import { useMaxTime } from '../../use-max-time';

interface DataType {
  services?: LangType[];
  values?: { goal: number; x: LangType; y: number }[];
  ignored_tasks: IgnoredTasksType[];
}

interface SeriesWithGoal {
  name: string;
  data: {
    x: string;
    y: number;
    goals: {
      name: string;
      value: number;
      strokeHeight: number;
      strokeColor: string;
    }[];
  }[];
}

export const useAverageTaskTimeByNodes = (): ChartProps => {
  const { t, getLocale } = useLang();
  const { maxTime, onChangeMaxTime, debouncedMaxTime } = useMaxTime();

  const { list } = useFetchDashboardData(['plan_fact_task_time'], {
    ignore_time_over_than: debouncedMaxTime,
  });

  const data = list?.plan_fact_task_time as DataType;

  const chartData = useMemo<Partial<ChartProps>>(() => {
    if (data) {
      const categories = data.values?.map((item) => getLocale(item.x)) ?? [];
      const colors = generateColors(categories?.length);
      const series = [{ name: t('Факт'), data: [] }] as SeriesWithGoal[];

      data.values?.forEach((value) => {
        series[0].data.push({
          x: getLocale(value.x),
          y: value.y,
          goals: [
            {
              name: t('План'),
              value: value.goal,
              strokeHeight: 5,
              strokeColor: '#775DD0',
            },
          ],
        });
      });

      const isDataExist = series.some((item) => item.data.some((data) => data.y > 0));

      return {
        series: isDataExist ? series : [],
        options: {
          chart: {
            type: 'bar',
          },
          plotOptions: {
            bar: {
              horizontal: false,
              distributed: true,
              borderRadius: 6,
              borderRadiusApplication: 'end',
            },
          },
          grid: {
            show: false,
          },
          xaxis: {
            axisBorder: { show: false },
            categories,
            axisTicks: { show: false },
            labels: {
              show: true,
              style: {
                colors,
                fontSize: '12px',
              },
            },
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '12px',
              },
            },
          },
          dataLabels: {
            enabled: false,
          },
          tooltip: {
            enabled: true,
            intersect: true,
          },
          legend: { show: false },
          colors,
        },
      };
    }

    return {
      series: [],
      options: {},
    };
  }, [data]);

  return {
    label: 'Сравнения планового и фактического времени выполнения задач',
    subLabel: 'Отчёт показывает сравнение исполнения задачи и его нормативного времени.',
    type: 'bar',
    wrapped: true,
    invalidData: {
      maxTime,
      onChangeMaxTime,
      ignoredTasks: data?.ignored_tasks,
    },
    series: chartData.series || [],
    options: withBarSelection(chartData).options || {},
  };
};
